import { FormatData, FormatService } from "./FormatService";
import {
    AnmaldArbete,
    AssistansErsattningOptions,
    DinMisstanke,
    MedvetetFel,
    RootState,
    Underrattelseskyldighet,
    VilkenRoll,
    VilkenSituation,
} from "@/store/types";
import {
    AnmaldArbeteText,
    AssistansErsattningText,
    MedvetetFelText,
    UnderrattelseskyldighetText,
    VilkenSituationText,
} from "@/store/map";
import { MatrixServiceId, MatrixServiceImpl } from "../MatrixService";
import { isFelaktigErsattningEnabled } from "@/utils";

export class MisstankeFormatService extends FormatService {
    private getSituation(situation: VilkenSituation | null): string {
        return situation ? this.$t(VilkenSituationText[situation]) : "";
    }

    private getVadStammerInPaPersonen(
        vadStammerInPaPersonen: AnmaldArbete | null,
    ): string {
        return vadStammerInPaPersonen
            ? this.$t(AnmaldArbeteText[vadStammerInPaPersonen])
            : "";
    }

    private getMedvetetFel(medvetetFel: MedvetetFel | null): string {
        return medvetetFel ? this.$t(MedvetetFelText[medvetetFel]) : "";
    }

    private getUnderrattelseskyldighet(
        underrattelseskyldighet: Underrattelseskyldighet | null,
    ): string {
        return underrattelseskyldighet
            ? this.$t(UnderrattelseskyldighetText[underrattelseskyldighet])
            : "";
    }

    private isTypAvErsattningEnabled(state: RootState): boolean {
        const { misstanke } = state.forsteg;
        const { situation } = state.observationer;
        if (misstanke && situation) {
            return isFelaktigErsattningEnabled(misstanke, situation);
        }
        return false;
    }

    private getAssistansersattning(state: RootState): string {
        const { assistansErsattningVal } =
            state.observationer.assistansErsattning;
        return assistansErsattningVal
            ? this.$t(AssistansErsattningText[assistansErsattningVal])
            : "";
    }

    private getBilagor(state: RootState): string {
        const { files } = state.observationer;
        return files
            .map((x) => this.removeIllegalCharacters(x.file.name))
            .join("\n");
    }

    public getHeader(): string {
        return `@s@${this.$t(
            "impulskontrollutredning.anmalan.alla.misstanke.generell.privat.rubrik.dinmisstanke",
        )}@!s@`;
    }

    // eslint-disable-next-line complexity
    public getFormData(state: RootState): Array<FormatData> {
        const textContext =
            state.forsteg.misstanke === DinMisstanke.PRIVATPERSON
                ? "privat"
                : "foretag";
        const vilkenRoll = state.forsteg.vilkenroll;
        const {
            situation,
            vadStammerInPaPersonen,
            privatpersonGenerell,
            foretagGenerell,
            utomlands,
            assistansErsattning,
            underrattelseskyldighet,
        } = state.observationer;
        const arbetarSamtidigt =
            situation === VilkenSituation.ARBETAR_SAMTIDIGT;
        const assistansersattning =
            situation === VilkenSituation.ASSISTENSERSATTNING;

        return [
            // Underrattelseskyldighet
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.misstanke.generell.alla.rubrik.underrattelseskyldighet",
                value: this.getUnderrattelseskyldighet(underrattelseskyldighet),
                isEnabled: vilkenRoll === VilkenRoll.MYNDIGHET,
            },
            // Vilken situation gäller misstanken?
            {
                textnyckel: `impulskontrollutredning.anmalan.alla.misstanke.generell.${textContext}.rubrik.situation`,
                value: this.getSituation(situation),
            },
            // Vilken typ av ersättning
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.generell.privat.rubrik.typaversattning",
                value: state.observationer.typAvErsattning,
                isEnabled:
                    vilkenRoll === VilkenRoll.MYNDIGHET ||
                    this.isTypAvErsattningEnabled(state),
            },
            // Är personen medveten?
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.generell.privat.rubrik.medvetetfel",
                value: this.getMedvetetFel(privatpersonGenerell.medvetetFel),
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.PERSON_MEDVETEN,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.generell.privat.rubrik.varfortrordudet",
                value: privatpersonGenerell.varforTrorDuDet,
                isEnabled:
                    MatrixServiceImpl.isEnabled(
                        MatrixServiceId.PERSON_MEDVETEN,
                        state,
                    ) && privatpersonGenerell.medvetetFel === MedvetetFel.JA,
            },
            // Är företaget medvetet?
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.generell.foretag.rubrik.medvetetfel",
                value: this.getMedvetetFel(foretagGenerell.medvetetFel),
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.FORETAG_MEDVETET,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.generell.foretag.rubrik.varfortrordudet",
                value: foretagGenerell.vadGorDetMedvetet,
                isEnabled:
                    MatrixServiceImpl.isEnabled(
                        MatrixServiceId.FORETAG_MEDVETET,
                        state,
                    ) && foretagGenerell.medvetetFel === MedvetetFel.JA,
            },
            // Arbetar samtidigt
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.vadstammerin",
                value: this.getVadStammerInPaPersonen(vadStammerInPaPersonen),
                isEnabled: arbetarSamtidigt,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.vilketforetag",
                value: state.observationer.anmaldArbete,
                isEnabled: arbetarSamtidigt,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.vilkenadress",
                value: state.observationer.anmaldArbeteAdress,
                isEnabled: arbetarSamtidigt,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.hurvetdu",
                value: state.observationer.anmaldHurVetDu,
                isEnabled: arbetarSamtidigt,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.hurmycketochofta",
                value: state.observationer.anmaldHurOftaArbetar,
                isEnabled: arbetarSamtidigt,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.beskrivarbete",
                value: state.observationer.anmaldBeskrivArbete,
                isEnabled: arbetarSamtidigt,
            },
            // Arbetar samtidigt verksamhet
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.beskrivforetag",
                value: state.observationer.anmaldBeskrivForetag,
                isEnabled:
                    arbetarSamtidigt &&
                    vadStammerInPaPersonen === AnmaldArbete.VERKSAMHET,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.foretagintresse",
                value: state.observationer.anmaldForetagIntressenter,
                isEnabled:
                    arbetarSamtidigt &&
                    vadStammerInPaPersonen === AnmaldArbete.VERKSAMHET,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.arbetarsamtidigt.privat.etikett.finnspersonmedinsyn",
                value: state.observationer.anmaldInsynIVerksamheten,
                isEnabled:
                    arbetarSamtidigt &&
                    vadStammerInPaPersonen === AnmaldArbete.VERKSAMHET,
            },
            // Sammanboende
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.sammanboende.privat.etikett.vilkenadress",
                value: state.observationer.ensamstaendeAdress,
                isEnabled: situation === VilkenSituation.SAMMANBOENDE,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.sammanboende.privat.etikett.hurvetsambo",
                value: state.observationer.ensamstaendeHurVetDu,
                isEnabled: situation === VilkenSituation.SAMMANBOENDE,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.sammanboende.privat.etikett.hurlangebottihop",
                value: state.observationer.ensamstaendeHurLange,
                isEnabled: situation === VilkenSituation.SAMMANBOENDE,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.sammanboende.privat.etikett.fornamnsambo",
                value: state.observationer.ensamstaendeFornamn,
                isEnabled: situation === VilkenSituation.SAMMANBOENDE,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.sammanboende.privat.etikett.efternamnsambo",
                value: state.observationer.ensamstaendeEfternamn,
                isEnabled: situation === VilkenSituation.SAMMANBOENDE,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.sammanboende.privat.etikett.lamnauppgifter",
                value: state.observationer.ensamstaendeUppgifterSambo,
                isEnabled: situation === VilkenSituation.SAMMANBOENDE,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.sammanboende.privat.etikett.ovrigabostader",
                value: state.observationer.ensamstaendeOvrigaBostader,
                isEnabled: situation === VilkenSituation.SAMMANBOENDE,
            },
            // Flyttat utomlands
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.flyttatutomlands.privat.etikett.vilketland",
                value: utomlands.vilketLand,
                isEnabled: situation === VilkenSituation.FLYTT_UTOMLANDS,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.flyttatutomlands.privat.etikett.hurvetutomlands",
                value: utomlands.hurVetDu,
                isEnabled: situation === VilkenSituation.FLYTT_UTOMLANDS,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.flyttatutomlands.privat.etikett.narutomlandsflytten",
                value: utomlands.narSkeddeFlytten,
                isEnabled: situation === VilkenSituation.FLYTT_UTOMLANDS,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.flyttatutomlands.privat.etikett.flyttatillbaka",
                value: utomlands.flyttaTillbaka,
                isEnabled: situation === VilkenSituation.FLYTT_UTOMLANDS,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.flyttatutomlands.privat.etikett.utomlandsvistelsen",
                value: utomlands.utomlandsVistelsen,
                isEnabled: situation === VilkenSituation.FLYTT_UTOMLANDS,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.flyttatutomlands.privat.etikett.vadvetombostadsverige",
                value: utomlands.bostadSverige,
                isEnabled: situation === VilkenSituation.FLYTT_UTOMLANDS,
            },
            // Assistansersättning vilken situation
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.generell.alla.etikett.vilkensituation",
                value: this.getAssistansersattning(state),
                isEnabled: assistansersattning,
            },
            // Assistansersättning utförs inte
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.utforsinte.alla.etikett.assistansersattningshjalp",
                value: assistansErsattning.vadFarPersonenHjalpMed,
                isEnabled:
                    assistansersattning &&
                    assistansErsattning.assistansErsattningVal ===
                        AssistansErsattningOptions.ASSISTANS_UTFORS_INTE,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.utforsinte.alla.etikett.berattaomassisansutforsinte",
                value: assistansErsattning.berattaOmAssistansen,
                isEnabled:
                    assistansersattning &&
                    assistansErsattning.assistansErsattningVal ===
                        AssistansErsattningOptions.ASSISTANS_UTFORS_INTE,
            },
            // Assistenten redovisar felaktigt
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.assistentfeltimmar.alla.etikett.intepersonligassistent",
                value: assistansErsattning.assistansTidInteArbetar,
                isEnabled:
                    assistansersattning &&
                    assistansErsattning.assistansErsattningVal ===
                        AssistansErsattningOptions.FELAKTIG_REDOVISNING,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.assistentfeltimmar.alla.etikett.intepersonligassistentannansysselsattning",
                value: assistansErsattning.assistansTidAnnanSysselsattning,
                isEnabled:
                    assistansersattning &&
                    assistansErsattning.assistansErsattningVal ===
                        AssistansErsattningOptions.FELAKTIG_REDOVISNING,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.assistentfeltimmar.alla.etikett.ekonomiskaoegentligheter",
                value: assistansErsattning.ekonomiskaOegentligheter,
                isEnabled:
                    assistansersattning &&
                    assistansErsattning.assistansErsattningVal ===
                        AssistansErsattningOptions.FELAKTIG_REDOVISNING,
            },
            // Assistansersättning låtsas behov
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.latsashabehov.alla.etikett.hurvetduingethjalpbehov",
                value: assistansErsattning.assistansErsattningEjBehov,
                isEnabled:
                    assistansersattning &&
                    assistansErsattning.assistansErsattningVal ===
                        AssistansErsattningOptions.LATSAS_BEHOV,
            },
            // Assistansersättning gemensamma fält
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.utforsinte.alla.etikett.skaltillmisstanke",
                value: assistansErsattning.beskrivOvrigaSkal,
                isEnabled: assistansersattning,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.generell.alla.etikett.hurlangefelutbetalt",
                value: assistansErsattning.hurLange,
                isEnabled: assistansersattning,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.generell.alla.etikett.uppgifterattkontrollera",
                value: assistansErsattning.uppgifterForsakringskassan,
                isEnabled: assistansersattning,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.generell.alla.etikett.finnsandrasomkannertill",
                value: assistansErsattning.andraPersoner,
                isEnabled: assistansersattning,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.assistansersattning.generell.alla.etikett.ovriginformation",
                value: assistansErsattning.ovrigInformation,
                isEnabled: assistansersattning,
            },
            // Annat misstanke privatperson
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.annat.privat.etikett.interattillersattning",
                value: state.observationer.annatMisstanke
                    .inteRattTillErsattning,
                isEnabled:
                    vilkenRoll === VilkenRoll.MYNDIGHET ||
                    situation === VilkenSituation.PRIVAT_ANNAN,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.annat.privat.etikett.hurlangefelutbetalt",
                value: state.observationer.annatMisstanke.hurLangeFelUtbetalt,
                isEnabled:
                    vilkenRoll === VilkenRoll.MYNDIGHET ||
                    situation === VilkenSituation.PRIVAT_ANNAN,
            },
            // Statligt tandvårdsstöd
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.statligttandvardsstod.foretag.etikett.beskrivsituation",
                value: state.observationer.statligtTandvardsstod
                    .beskrivSituationen,
                isEnabled: situation === VilkenSituation.STATLIGT_TANDVARDSSTOD,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.statligttandvardsstod.foretag.etikett.besok",
                value: state.observationer.statligtTandvardsstod
                    .fleraEllerSpeciellaBesok,
                isEnabled: situation === VilkenSituation.STATLIGT_TANDVARDSSTOD,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.statligttandvardsstod.foretag.etikett.narbesok",
                value: state.observationer.statligtTandvardsstod
                    .narGjordesBesoken,
                isEnabled: situation === VilkenSituation.STATLIGT_TANDVARDSSTOD,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.statligttandvardsstod.foretag.etikett.namnpatandlakare",
                value: state.observationer.statligtTandvardsstod
                    .namnPaTandlakare,
                isEnabled: situation === VilkenSituation.STATLIGT_TANDVARDSSTOD,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.statligttandvardsstod.foretag.etikett.namnpamottagning",
                value: state.observationer.statligtTandvardsstod
                    .namnPaMottagning,
                isEnabled: situation === VilkenSituation.STATLIGT_TANDVARDSSTOD,
            },
            // Annat misstanke företag
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.annat.foretag.etikett.beskrivsituation",
                value: state.observationer.foretagGenerell.beskrivSituationen,
                isEnabled: situation === VilkenSituation.FORETAG_ANNAN,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.annat.foretag.etikett.hurvetdu",
                value: state.observationer.foretagGenerell.hurVetDuAtt,
                isEnabled: situation === VilkenSituation.FORETAG_ANNAN,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.misstanke.annat.foretag.etikett.lamnauppgifter",
                value: state.observationer.foretagGenerell
                    .lamnaUppgifterSomStodjer,
                isEnabled: situation === VilkenSituation.FORETAG_ANNAN,
            },
            // Bilagor
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.misstanke.kvittens.alla.utskrift.bilagor",
                value: this.getBilagor(state),
            },
        ];
    }
}
